import { useState } from "react";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import { MenuItem } from "@headlessui/react";
import { cn } from "@/lib/utils";
import { Spinner } from "@/lib/Components/Layout/Loaders/Spinner";
import type { Action } from "@/lib/Components/Actions/ActionGroupButtons";
import { ButtonColor } from "@/components/catalyst/button";
import { Badge } from "@/components/catalyst/badge";

const colors: Partial<
  Record<ButtonColor, { active: string; default: string }>
> = {
  pink: {
    active: "bg-pink-50 text-pink-700",
    default: "text-pink-600",
  },
  indigo: {
    active: "bg-indigo-50 text-indigo-700",
    default: "text-indigo-600",
  },
  red: {
    active: "bg-red-50 text-red-700",
    default: "text-red-600",
  },
  green: {
    active: "bg-green-50 text-green-700",
    default: "text-green-600",
  },
  yellow: {
    active: "bg-amber-50 text-amber-700",
    default: "text-amber-600",
  },
  blue: {
    active: "bg-blue-50 text-blue-700",
    default: "text-blue-600",
  },
  purple: {
    active: "bg-purple-50 text-purple-700",
    default: "text-purple-600",
  },
  orange: {
    active: "bg-orange-50 text-orange-700",
    default: "text-orange-600",
  },
};

const defaultColor = {
  active: "bg-gray-50 text-gray-900",
  default: "text-gray-700",
};

export function ActionMenuItem({
  Icon,
  label: actionLabel,
  handleAction,
  color,
  isHidden,
  isAdminAction,
}: Action) {
  const [isLoading, setIsLoading] = useState(false);
  const { isAdmin } = useTenant();

  if (isAdminAction && !isAdmin) return null;
  if (isHidden) return null;

  const colorStyles = (color && colors[color]) ?? defaultColor;

  return (
    <MenuItem as="div">
      {({ focus, close }) => {
        return (
          <button
            type="button"
            className={cn(
              focus ? colorStyles.active : colorStyles.default,
              "group flex w-full min-w-0 items-center px-4 py-2 text-left text-sm",
            )}
            onClick={async (e) => {
              e.preventDefault();
              setIsLoading(true);
              try {
                await handleAction();
                close();
              } catch (e: any) {
                console.error(e);
              } finally {
                setIsLoading(false);
              }
            }}
          >
            <span className="mr-3 shrink-0">
              {isLoading ? (
                <Spinner />
              ) : (
                <>
                  {Icon && (
                    <Icon
                      className={cn("h-5 w-5", colorStyles.default)}
                      aria-hidden="true"
                    />
                  )}
                </>
              )}
            </span>
            <span className="grow truncate">{actionLabel}</span>
            {isAdminAction ? <Badge color="sky">admin</Badge> : null}
          </button>
        );
      }}
    </MenuItem>
  );
}
